/**
 * Application shell: slim rail (icon over a short name) + adjacent detail panel + a thin header over the content
 * column only (search and shortcuts; it never covers the rail or panel) + thin status footer.
 *
 * The rail lists the areas; the panel shows only the pages of the selected (or, as an overlay,
 * the hovered/focused) area, so it never repeats the rail.
 *
 * Modes (persisted per membership, see useMembershipPreference("nav_mode")):
 *  - pinned:    panel docked beside the rail; content reflows.
 *  - auto:      panel opens as an overlay on hover-intent, keyboard focus or click; closes after
 *               navigation, pointer leave, Escape or outside click.
 *  - collapsed: rail only; the panel opens only through the explicit "Show navigation" button.
 * Touch/mobile (<768px): top bar + accessible drawer (Radix Dialog), never hover.
 */
import { ChevronsRight, Menu as MenuIcon, PanelLeft, X } from "lucide-react";
import { Dialog as D } from "radix-ui";
import { useCallback, useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";

import { IconButton, Menu, MenuLabel, MenuRadioGroup, Tooltip } from "@/components/ui";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cx } from "@/lib/cx";
import { frontendBuild } from "@/lib/version";

import type { NavMode, NavSection } from "./nav";

const OPEN_DELAY = 120;
const CLOSE_DELAY = 280;

type AppShellProps = {
  sections: NavSection[];
  mode: NavMode;
  onModeChange: (mode: NavMode) => void;
  /** Workspace identity/switcher at the top of the rail (receives compact=true) and panel. */
  workspace: (compact: boolean) => ReactNode;
  workspaceLabel: string;
  /** Tools above the navigation panel button in the rail (the assistant). */
  railTools?: ReactNode;
  /** Footer controls in the rail (profile menu, What's New...). */
  railFooter: ReactNode;
  banner?: ReactNode;
  /** Header content over the page (search, quick create); compact=true in the phone top bar. */
  header?: (compact: boolean) => ReactNode;
  /** Small status shown in the footer (e.g. the live-updates connection). */
  status?: ReactNode;
  children: ReactNode;
};

function activeSectionKey(sections: NavSection[], pathname: string): string | undefined {
  const prefix = (s: NavSection) => s.base ?? s.to;
  const match = [...sections]
    .sort((a, b) => prefix(b).length - prefix(a).length)
    .find((s) => {
      const p = prefix(s);
      return s.end ? pathname === p || pathname === `${p}/` : pathname === p || pathname.startsWith(`${p}/`);
    });
  return match?.key;
}

export function AppShell({ sections, mode, onModeChange, workspace, workspaceLabel, railTools, railFooter, banner, header, status, children }: AppShellProps) {
  const isMobile = useIsMobile();
  const location = useLocation();
  const currentKey = activeSectionKey(sections, location.pathname) ?? sections[0]?.key;
  // Overlay/drawer state is tied to the path it was opened on, so navigating closes it (derived, no effect).
  const [overlay, setOverlay] = useState<{ key: string; path: string } | null>(null);
  const [drawerPath, setDrawerPath] = useState<string | null>(null);
  const overlayKey = overlay?.path === location.pathname ? overlay.key : null;
  const mobileOpen = drawerPath === location.pathname;
  const setOverlayKey = useCallback((key: string | null) => setOverlay(key ? { key, path: location.pathname } : null), [location.pathname]);
  const setMobileOpen = (open: boolean) => setDrawerPath(open ? location.pathname : null);
  const timers = useRef<{ open?: ReturnType<typeof setTimeout>; close?: ReturnType<typeof setTimeout> }>({});
  const railRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const pinned = mode === "pinned" && !isMobile;
  const currentLabel = sections.find((s) => s.key === currentKey)?.label;
  useEffect(() => {
    document.title = [currentLabel, workspaceLabel, "K-SHOP"].filter(Boolean).join(" · ");
  }, [currentLabel, workspaceLabel]);
  const panelKey = overlayKey ?? currentKey;
  const panelSection = sections.find((s) => s.key === panelKey);

  const clearTimers = () => {
    clearTimeout(timers.current.open);
    clearTimeout(timers.current.close);
  };
  const closeOverlay = useCallback(() => {
    clearTimeout(timers.current.open);
    clearTimeout(timers.current.close);
    setOverlay(null);
  }, []);

  // Escape closes the overlay panel and returns focus to its rail item.
  useEffect(() => {
    if (!overlayKey) return;
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      closeOverlay();
      railRef.current?.querySelector<HTMLElement>(`[data-rail-item="${overlayKey}"]`)?.focus();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [overlayKey, closeOverlay]);

  // Outside click closes the overlay panel.
  useEffect(() => {
    if (!overlayKey) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!railRef.current?.contains(target) && !panelRef.current?.contains(target)) closeOverlay();
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [overlayKey, closeOverlay]);

  // Ctrl/Cmd+B toggles pinned/collapsed. A modifier combination (not a single character, WCAG
  // 2.1.4); ignored while typing or while a dialog is open.
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key.toLowerCase() !== "b" || !(event.metaKey || event.ctrlKey) || event.altKey || event.shiftKey) return;
      const el = event.target;
      if (el instanceof Element && el.closest("input, textarea, select, [contenteditable=true]")) return;
      if (document.querySelector('[role="dialog"], [role="alertdialog"]')) return;
      event.preventDefault();
      onModeChange(mode === "pinned" ? "collapsed" : "pinned");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mode, onModeChange]);

  const requestOpen = (key: string, immediate = false) => {
    if (pinned) return;
    clearTimers();
    if (immediate || overlayKey) setOverlayKey(key);
    else timers.current.open = setTimeout(() => setOverlayKey(key), OPEN_DELAY);
  };
  const requestClose = () => {
    clearTimeout(timers.current.open);
    if (!overlayKey) return;
    timers.current.close = setTimeout(() => setOverlayKey(null), CLOSE_DELAY);
  };

  // An overlay panel must not cover content that keyboard focus moved into (WCAG 2.4.11).
  const closeIfFocusLeft = (next: EventTarget | null) => {
    if (!overlayKey || !(next instanceof Node)) return;
    if (!railRef.current?.contains(next) && !panelRef.current?.contains(next)) closeOverlay();
  };

  const onRailKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    const links = Array.from(railRef.current?.querySelectorAll<HTMLElement>("[data-rail-item]") ?? []);
    const index = links.indexOf(event.currentTarget);
    if (index === -1) return;
    const next =
      event.key === "ArrowDown" ? links[(index + 1) % links.length]
      : event.key === "ArrowUp" ? links[(index - 1 + links.length) % links.length]
      : event.key === "Home" ? links[0]
      : event.key === "End" ? links[links.length - 1]
      : event.key === "ArrowRight" && !pinned ? null
      : undefined;
    if (next === undefined) return;
    event.preventDefault();
    if (next === null) {
      // Move into the (overlay) panel.
      setOverlayKey(links[index].dataset.railItem ?? null);
      requestAnimationFrame(() => panelRef.current?.querySelector<HTMLElement>("a")?.focus());
      return;
    }
    next.focus();
  };

  const modeControl = (
    <Menu
      label="Navigation display"
      side="right"
      align="end"
      trigger={
        <button
          type="button"
          data-tour="nav-mode"
          aria-label={`Navigation display: ${mode}`}
          className="flex size-9 items-center justify-center rounded-md text-fg-muted transition-colors transition-fast hover:bg-surface-hover hover:text-fg"
        >
          <PanelLeft aria-hidden className="size-[18px]" />
        </button>
      }
    >
      <MenuLabel>Navigation panel</MenuLabel>
      <MenuRadioGroup<NavMode>
        value={mode}
        onValueChange={onModeChange}
        options={[
          { value: "pinned", label: "Keep open", description: "Panel stays beside the rail (Ctrl/⌘ B)" },
          { value: "auto", label: "Open on hover", description: "Opens on hover, focus or click" },
          { value: "collapsed", label: "Collapsed", description: "Open only when you ask" },
        ]}
      />
    </Menu>
  );

  if (isMobile) {
    return (
      <div className="flex h-dvh flex-col">
        {banner}
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-2">
          <D.Root open={mobileOpen} onOpenChange={setMobileOpen}>
            <D.Trigger asChild>
              <IconButton label="Open navigation" icon={<MenuIcon className="size-5" />} tooltip={false} />
            </D.Trigger>
            <D.Portal>
              <D.Overlay className="animate-fade-in fixed inset-0 z-40 bg-overlay" />
              <D.Content className="animate-sheet-in fixed inset-y-0 left-0 z-50 flex w-[min(20rem,85vw)] flex-col border-r border-line bg-surface shadow-lg">
                <D.Title className="sr-only">Navigation</D.Title>
                <D.Description className="sr-only">{workspaceLabel} navigation</D.Description>
                <div className="flex items-center justify-between gap-2 border-b border-line p-2">
                  <div className="min-w-0 flex-1">{workspace(false)}</div>
                  <D.Close asChild>
                    <IconButton label="Close navigation" icon={<X className="size-4" />} tooltip={false} />
                  </D.Close>
                </div>
                <nav aria-label="Main" className="flex-1 overflow-y-auto p-2">
                  <SectionTree sections={sections} currentKey={currentKey} />
                </nav>
                <div className="flex items-center justify-between border-t border-line p-2">
                  <div className="flex items-center gap-1">{railFooter}</div>
                  <span className="flex flex-col items-end gap-0.5 text-xs text-fg-subtle tabular">
                    {status}
                    <span>K-SHOP v{frontendBuild.version}</span>
                  </span>
                </div>
              </D.Content>
            </D.Portal>
          </D.Root>
          <div className="min-w-0 flex-1 truncate text-sm font-semibold">{panelSection?.label ?? workspaceLabel}</div>
          {header && <div className="flex shrink-0 items-center gap-1">{header(true)}</div>}
        </header>
        {/* `relative`: absolutely positioned content (e.g. sr-only text) must scroll with <main>, not grow the document. */}
        <main id="main" className="@container relative min-h-0 flex-1 overflow-y-auto">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col">
      <a
        href="#main"
        className="sr-only z-[70] rounded-md bg-surface px-3 py-2 text-sm font-medium focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:shadow-md"
      >
        Skip to content
      </a>
      {banner}
      <div className="relative flex min-h-0 flex-1">
        <div
          ref={railRef}
          onPointerLeave={requestClose}
          onPointerEnter={() => clearTimeout(timers.current.close)}
          onBlur={(e) => closeIfFocusLeft(e.relatedTarget)}
          className="z-30 flex w-[var(--rail-width)] shrink-0 flex-col items-center border-r border-line bg-rail py-2"
        >
          <div className="mb-2">{workspace(true)}</div>
          {/* Scrolls (without a visible scrollbar) when the areas don't fit a short window. */}
          <nav aria-label="Main" data-tour="nav-rail" className="scrollbar-none flex min-h-0 w-full flex-1 flex-col items-center gap-1 overflow-y-auto px-1 py-0.5">
            {sections.map((section) => {
              const active = section.key === currentKey;
              const Icon = section.icon;
              // A shortened rail name keeps the full name as the accessible name and in a tooltip.
              const shortened = Boolean(section.railLabel) && section.railLabel !== section.label;
              const link = (
                <NavLink
                  key={section.key}
                  to={section.to}
                  end={section.end}
                  data-rail-item={section.key}
                  aria-label={shortened ? section.label : undefined}
                  aria-current={active ? "page" : undefined}
                  onKeyDown={onRailKeyDown}
                  onPointerEnter={() => mode === "auto" && requestOpen(section.key)}
                  onFocus={(e) => mode === "auto" && e.currentTarget.matches(":focus-visible") && requestOpen(section.key, true)}
                  className={cx(
                    "relative flex w-full shrink-0 flex-col items-center gap-1 rounded-md pt-1.5 pb-1 transition-colors transition-fast",
                    active ? "bg-accent-subtle text-accent-text" : "text-fg-muted hover:bg-surface-hover hover:text-fg",
                    overlayKey === section.key && !active && "bg-surface-hover text-fg",
                  )}
                >
                  {active && <span aria-hidden className="absolute top-1.5 bottom-1.5 -left-1 w-[3px] rounded-r-sm bg-accent-indicator" />}
                  <Icon aria-hidden className="size-[18px] shrink-0" strokeWidth={active ? 2.2 : 1.9} />
                  <span className={cx("line-clamp-2 w-full text-center text-[11px] leading-[13px] hyphens-manual break-words", active && "font-medium")}>
                    {section.railLabel ?? section.label}
                  </span>
                </NavLink>
              );
              return shortened ? (
                <Tooltip key={section.key} content={section.label} side="right">
                  {link}
                </Tooltip>
              ) : (
                link
              );
            })}
          </nav>
          <div className="flex flex-col items-center gap-1">
            {railTools}
            {!pinned && (
              <IconButton
                label="Show navigation panel"
                tooltipSide="right"
                icon={<ChevronsRight className="size-[18px]" />}
                aria-expanded={Boolean(overlayKey)}
                onClick={() => (overlayKey ? closeOverlay() : currentKey && requestOpen(currentKey, true))}
              />
            )}
            {modeControl}
            {railFooter}
          </div>
        </div>

        {(pinned || overlayKey) && panelSection && (
          <div
            ref={panelRef}
            onPointerEnter={() => clearTimeout(timers.current.close)}
            onPointerLeave={() => !pinned && requestClose()}
            onBlur={(e) => closeIfFocusLeft(e.relatedTarget)}
            className={cx(
              "flex w-[var(--panel-width)] shrink-0 flex-col border-r border-line bg-surface",
              pinned ? "relative" : "animate-panel-in absolute inset-y-0 left-[var(--rail-width)] z-20 shadow-lg",
            )}
          >
            <div className="border-b border-line px-3 py-2.5">{workspace(false)}</div>
            <SectionPages section={panelSection} navLabel={`${workspaceLabel} navigation`} />
          </div>
        )}

        <div className="flex min-w-0 flex-1 flex-col">
          {header && (
            <header aria-label="Workspace tools" className="flex h-11 shrink-0 items-center gap-3 border-b border-line bg-surface px-4">
              {header(false)}
            </header>
          )}
          {/* `relative`: absolutely positioned content (e.g. sr-only text) must scroll with <main>, not grow the document. */}
          <main id="main" tabIndex={-1} className="@container relative min-h-0 min-w-0 flex-1 overflow-y-auto outline-none">
            {children}
          </main>
        </div>
      </div>
      <footer className="flex h-6 shrink-0 items-center justify-end gap-4 border-t border-line bg-rail px-3 text-xs text-fg-subtle tabular">
        {status}
        <span>K-SHOP v{frontendBuild.version}</span>
      </footer>
    </div>
  );
}

/** The panel content for one section: a title bar with its name, then its own pages. */
function SectionPages({ section, navLabel }: { section: NavSection; navLabel: string }) {
  const labelId = `nav-section-${section.key}`;
  const Icon = section.icon;
  return (
    <>
      {/* A title bar, not a link: its own tinted strip with the area's icon, set apart from the links by a
          divider. Not a heading element, so the page's own h1 stays the first heading in reading order. */}
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-line bg-surface-sunken px-3 select-none">
        <Icon aria-hidden className="size-4 shrink-0 text-accent-text" strokeWidth={2.2} />
        <p id={labelId} className="truncate text-sm font-semibold text-fg">
          {section.label}
        </p>
        {section.planned && <PlannedTag />}
      </div>
      <nav aria-label={navLabel} className="flex-1 overflow-y-auto p-2">
        <ul aria-labelledby={labelId} className="flex flex-col gap-0.5">
          {section.items.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end ?? true}
                className={({ isActive }) =>
                  cx(
                    "flex h-8 items-center rounded-md px-2 text-sm transition-colors transition-fast",
                    isActive ? "bg-accent-subtle font-medium text-accent-text" : "text-fg-muted hover:bg-surface-hover hover:text-fg",
                  )
                }
              >
                <span className="truncate">{item.label}</span>
                {item.planned && !section.planned && <PlannedTag />}
                {item.badge ? <CountTag count={item.badge} /> : null}
              </NavLink>
            </li>
          ))}
        </ul>
        {section.recent && section.recent.length > 0 && (
          <div className="mt-3 border-t border-line pt-3">
            <p id={`${labelId}-recent`} className="mb-1 px-2 text-xs font-medium tracking-wide text-fg-muted uppercase">
              Recent
            </p>
            <ul aria-labelledby={`${labelId}-recent`} className="flex flex-col gap-0.5">
              {section.recent.map((record) => (
                <li key={record.to}>
                  <NavLink
                    to={record.to}
                    title={`${record.kind}: ${record.label}`}
                    className={({ isActive }) =>
                      cx(
                        "flex min-h-8 flex-col justify-center rounded-md px-2 py-1 text-sm transition-colors transition-fast",
                        isActive ? "bg-accent-subtle font-medium text-accent-text" : "text-fg-muted hover:bg-surface-hover hover:text-fg",
                      )
                    }
                  >
                    <span className="truncate">{record.label}</span>
                    <span className="truncate text-xs text-fg-muted">{record.kind}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>
    </>
  );
}

/** Phone drawer (no rail): every section, each with its pages when it has more than one. */
function SectionTree({ sections, currentKey }: { sections: NavSection[]; currentKey?: string }) {
  return (
    <ul className="flex flex-col gap-0.5">
      {sections.map((section) => {
        const Icon = section.icon;
        const current = section.key === currentKey;
        const expanded = section.items.length > 1;
        return (
          <li key={section.key}>
            <NavLink
              to={section.to}
              end={section.end}
              aria-current={current ? "page" : undefined}
              className={cx(
                "flex h-8 items-center gap-2.5 rounded-md px-2 text-sm transition-colors transition-fast",
                current ? "font-medium text-fg" : "text-fg-muted hover:bg-surface-hover hover:text-fg",
                current && !expanded && "bg-accent-subtle text-accent-text",
              )}
            >
              <Icon aria-hidden className={cx("size-4 shrink-0", current ? "text-accent-text" : "text-fg-subtle")} />
              <span className="truncate">{section.label}</span>
              {section.planned && <PlannedTag />}
            </NavLink>
            {expanded && (
              <ul className="mt-0.5 mb-1.5 ml-[18px] flex flex-col gap-0.5 border-l border-line pl-2.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end ?? true}
                      className={({ isActive }) =>
                        cx(
                          "flex h-7 items-center rounded-md px-2 text-sm transition-colors transition-fast",
                          isActive ? "bg-accent-subtle font-medium text-accent-text" : "text-fg-muted hover:bg-surface-hover hover:text-fg",
                        )
                      }
                    >
                      <span className="truncate">{item.label}</span>
                      {item.planned && !section.planned && <PlannedTag />}
                      {item.badge ? <CountTag count={item.badge} /> : null}
                    </NavLink>
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** A count on a page link (e.g. requests waiting for review); the number is read with the link. */
function CountTag({ count }: { count: number }) {
  return (
    <span className="ml-auto shrink-0 rounded-full bg-accent-subtle px-1.5 text-xs font-medium text-accent-text tabular">
      {count > 99 ? "99+" : count}
      <span className="sr-only"> waiting</span>
    </span>
  );
}

/** Marks a link to an area that isn't built yet (it opens the "Under construction" page). */
function PlannedTag() {
  return <span className="ml-auto shrink-0 pl-2 text-xs font-normal text-fg-subtle">Planned</span>;
}
